import React from 'react';
import * as Sentry from '@sentry/react';
import { Box } from '@mui/material';
import ColoredAlert from '../ColoredAlert';

type Props = {
  height: number;
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  retryKey: number;
};

class DocumentErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'document_viewer');
      scope.setContext('react', {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });
  }

  retry = () => {
    this.setState(({ retryKey }) => ({
      hasError: false,
      retryKey: retryKey + 1,
    }));
  };

  render() {
    const { children, height } = this.props;
    const { hasError, retryKey } = this.state;

    if (hasError) {
      return (
        <Box height={height} display="flex" alignItems="center">
          <ColoredAlert
            type="error"
            title="Couldn't display the document"
            subtitle="Something went wrong while loading the PDF."
            dismissible={false}
            animate={false}
            button={{ text: 'Try again', onClick: this.retry }}
          />
        </Box>
      );
    }

    return <React.Fragment key={retryKey}>{children}</React.Fragment>;
  }
}

export default DocumentErrorBoundary;
